import React, { useState, useMemo } from 'react';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { Users, ChevronLeft, ChevronRight, Search, X, UserCheck, Trash2 } from 'lucide-react';
import { EmployeeListItem } from './EmployeeChip';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';

/**
 * EmployeePalette - Rechte Seitenleiste mit allen Mitarbeitern.
 *
 * Features:
 * - Tab "Alle": Alle aktiven Mitarbeiter gruppiert nach KSt
 * - Tab "Frei heute": Mitarbeiter, die heute weder eingeplant noch abwesend sind
 * - Filter: KSt, Suche
 * - D&D aus Plantafel: Chip in Palette droppen → Einsatz wird entfernt
 * - Kollabierbar
 */
export function EmployeePalette({ employees, costCenters, assignments = [], absences = [], todayIso, istHeute = true, collapsed, onToggleCollapsed, activeDrag }) {
  const [search, setSearch] = useState('');
  const [kstFilter, setKstFilter] = useState('all');
  const [tab, setTab] = useState('all');

  // Trash-Drop: aktiviert wenn ein Chip (nicht Palette-MA) gedragged wird
  const trashActive = typeof activeDrag === 'string' && !activeDrag.startsWith('palette::');
  const { setNodeRef: setTrashRef, isOver: isTrashOver } = useDroppable({
    id: 'palette-trash',
    disabled: !trashActive,
  });

  // Baustellen-Leute. Geräte-Mitarbeiter haben ihre eigene Plantafel, Büro
  // und Leitung werden gar nicht eingeplant – beide gehören hier nicht in
  // die Auswahl, sonst zieht sie jemand versehentlich auf eine Baustelle.
  const activeEmps = useMemo(
    () => employees.filter(e => e.active && !['device', 'office'].includes(e.assignment_context)),
    [employees]
  );

  const today = todayIso || new Date().toISOString().slice(0, 10);

  // Arbeitet derjenige heute überhaupt? Kommt aus der Urlaubsrichtlinie
  // (work_weekdays, Montag=0). Ohne Richtlinie gilt Montag bis Freitag.
  //
  // Hintergrund: Eine Teilzeitkraft mit Mo–Mi stand hier auch donnerstags
  // unter "Frei heute" – also als verfügbar, obwohl sie gar nicht kommt.
  // "Frei heute" heißt nur heute wirklich heute. Steht der Fokus auf einem
  // anderen Tag, muss das Datum im Titel stehen – sonst glaubt man, die Liste
  // gelte für heute, und besetzt den falschen Tag.
  const datumKurz = useMemo(() => {
    const [j, m, t] = today.split('-');
    return `${t}.${m}.`;
  }, [today]);
  const freiTitel = istHeute ? 'Frei heute' : `Frei ${datumKurz}`;

  const heuteWochentag = useMemo(() => {
    const d = new Date(today + 'T00:00:00');
    return (d.getDay() + 6) % 7; // 0 = Montag
  }, [today]);

  const arbeitetHeute = (e) => {
    const tage = e.work_weekdays && e.work_weekdays.length ? e.work_weekdays : [0, 1, 2, 3, 4];
    return tage.includes(heuteWochentag);
  };

  // Set of employee_ids that have an assignment today OR are fully absent today
  const busyEmpIdsToday = useMemo(() => {
    const busy = new Set();
    for (const a of assignments) {
      if (a.date === today) busy.add(a.employee_id);
    }
    for (const ab of absences) {
      if (ab.date === today && !ab.half_day) busy.add(ab.employee_id);
    }
    return busy;
  }, [assignments, absences, today]);

  const filteredBase = useMemo(() => {
    return activeEmps.filter(e => {
      if (kstFilter !== 'all' && e.cost_center_id !== kstFilter) return false;
      if (search.trim() && !`${e.first_name} ${e.last_name}`.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [activeEmps, kstFilter, search]);

  const filtered = useMemo(() => {
    if (tab === 'unassigned') {
      // "Frei heute" heißt verfügbar. Wer heute laut Richtlinie nicht
      // arbeitet, ist nicht frei – er ist nicht da.
      return filteredBase.filter(e => !busyEmpIdsToday.has(e.id) && arbeitetHeute(e));
    }
    return filteredBase;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredBase, tab, busyEmpIdsToday, heuteWochentag]);

  const groupedByKst = useMemo(() => {
    const map = new Map();
    for (const kst of costCenters.filter(c => !c.deleted && c.active)) {
      map.set(kst.id, { kst, items: [] });
    }
    for (const e of filtered) {
      if (!map.has(e.cost_center_id)) continue;
      map.get(e.cost_center_id).items.push(e);
    }
    return Array.from(map.values()).filter(g => g.items.length > 0);
  }, [filtered, costCenters]);

  const kstFilterItems = [
    { id: 'all', label: 'Alle', color: '#64748b' },
    ...costCenters.filter(c => !c.deleted && c.active).map(c => ({ id: c.id, label: c.short_code, color: c.color })),
  ];

  const unassignedCount = activeEmps.filter(e => !busyEmpIdsToday.has(e.id) && arbeitetHeute(e)).length;

  if (collapsed) {
    return (
      <div className="palette-panel no-print flex flex-col items-center" style={{ width: 40 }}>
        <button
          onClick={onToggleCollapsed}
          className="w-full h-12 flex items-center justify-center text-slate-600 hover:bg-slate-100 border-b border-slate-200"
          title="Mitarbeiter-Palette einblenden"
          data-testid="palette-expand-button"
        >
          <ChevronLeft size={16} />
        </button>
        <div
          ref={setTrashRef}
          className={`w-full ${trashActive ? 'bg-rose-50 border-b border-rose-300' : 'border-b border-slate-200'} ${isTrashOver ? 'bg-rose-100 ring-2 ring-rose-400' : ''}`}
          style={{ display: trashActive ? 'flex' : 'none', minHeight: 48 }}
          title="Chip hier ablegen zum Entfernen"
          data-testid="palette-trash-collapsed"
        >
          <Trash2 size={20} className="text-rose-600 m-auto"/>
        </div>
        <div className="flex flex-col items-center py-2 gap-2">
          <Users size={16} className="text-slate-500" />
          <div className="text-[10px] font-mono text-slate-500 writing-mode-vertical" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>
            Mitarbeiter
          </div>
          <Badge className="text-[9px] px-1 mt-2">{activeEmps.length}</Badge>
          {unassignedCount > 0 && (
            <Badge className="text-[9px] px-1 bg-emerald-600 hover:bg-emerald-600" title={freiTitel}>{unassignedCount}</Badge>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="palette-panel no-print flex flex-col" style={{ width: 240 }}>
      <div className="h-10 flex items-center justify-between px-3 border-b border-slate-200 flex-shrink-0">
        <div className="flex items-center gap-1.5 text-sm font-semibold font-display">
          <Users size={14} /> Mitarbeiter
        </div>
        <button
          onClick={onToggleCollapsed}
          className="p-1 text-slate-500 hover:text-slate-900 rounded hover:bg-slate-100"
          title="Palette einklappen"
          data-testid="palette-collapse-button"
        >
          <ChevronRight size={14} />
        </button>
      </div>

      {/* Trash-Drop-Zone - nur sichtbar wenn ein Chip gedragged wird */}
      {trashActive && (
        <div
          ref={setTrashRef}
          className={`m-2 rounded border-2 border-dashed transition-all ${isTrashOver ? 'border-rose-500 bg-rose-100' : 'border-rose-300 bg-rose-50'} flex items-center justify-center gap-2 py-4`}
          data-testid="palette-trash"
        >
          <Trash2 size={16} className="text-rose-600"/>
          <span className="text-xs font-semibold text-rose-700">
            {isTrashOver ? 'Loslassen zum Entfernen' : 'Hier ablegen zum Entfernen'}
          </span>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab} className="flex-1 flex flex-col min-h-0">
        <TabsList className="mx-2 mt-2 grid grid-cols-2 h-7" data-testid="palette-tabs">
          <TabsTrigger value="all" className="text-[11px] h-5" data-testid="palette-tab-all">
            Alle <Badge variant="outline" className="ml-1 text-[9px] px-1 h-4">{filteredBase.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="unassigned" className="text-[11px] h-5" data-testid="palette-tab-unassigned">
            <UserCheck size={10} className="mr-0.5"/>{freiTitel} <Badge variant="outline" className="ml-1 text-[9px] px-1 h-4">{unassignedCount}</Badge>
          </TabsTrigger>
        </TabsList>

        <div className="p-2 space-y-2 border-b border-slate-200 mt-2">
          <div className="relative">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Suchen…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-6 h-7 text-xs"
              data-testid="palette-search-input"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-slate-400 hover:text-slate-900">
                <X size={10} />
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-1">
            {kstFilterItems.map(item => (
              <button
                key={item.id}
                onClick={() => setKstFilter(item.id)}
                className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${kstFilter === item.id ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'}`}
                data-testid={`palette-filter-${item.label}`}
              >
                {item.id !== 'all' && <span className="inline-block w-2 h-2 rounded-sm mr-1 align-middle" style={{ background: item.color }}></span>}
                {item.label}
              </button>
            ))}
          </div>
          <div className="text-[10px] text-slate-500 leading-tight">
            {tab === 'unassigned'
              ? `Ohne Einsatz und Abwesenheit am ${datumKurz}, und laut Richtlinie im Dienst.`
              : `In Plantafel-Zelle oder direkt auf ein Projekt ziehen. Ausgegraut = arbeitet am ${datumKurz} laut Richtlinie nicht.`}
          </div>
        </div>

        <TabsContent value="all" className="flex-1 overflow-y-auto scroll-thin p-2 space-y-3 mt-0">
          {groupedByKst.length === 0 ? (
            <div className="text-xs text-slate-400 text-center py-6">Keine Mitarbeiter gefunden.</div>
          ) : (
            groupedByKst.map(group => (
              <div key={group.kst.id}>
                <div className="flex items-center gap-1.5 mb-1.5 px-1">
                  <span className="w-2.5 h-2.5 rounded-sm" style={{ background: group.kst.color }}></span>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{group.kst.short_code}</span>
                  <span className="text-[10px] text-slate-400 ml-auto font-mono">{group.items.length}</span>
                </div>
                <div className="space-y-1">
                  {group.items.map(emp => (
                    <PaletteDraggable key={emp.id} employee={emp} costCenter={group.kst}
                                      arbeitetNicht={!arbeitetHeute(emp)} policyName={emp.policy_name} />
                  ))}
                </div>
              </div>
            ))
          )}
        </TabsContent>

        <TabsContent value="unassigned" className="flex-1 overflow-y-auto scroll-thin p-2 space-y-3 mt-0">
          {groupedByKst.length === 0 ? (
            <div className="text-xs text-slate-400 text-center py-6">
              <UserCheck size={20} className="mx-auto mb-1 text-emerald-500"/>
              {istHeute
                ? 'Alle Mitarbeiter sind heute verplant oder abwesend.'
                : `Am ${datumKurz} ist niemand mehr frei.`}
            </div>
          ) : (
            groupedByKst.map(group => (
              <div key={group.kst.id}>
                <div className="flex items-center gap-1.5 mb-1.5 px-1">
                  <span className="w-2.5 h-2.5 rounded-sm" style={{ background: group.kst.color }}></span>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{group.kst.short_code}</span>
                  <span className="text-[10px] text-slate-400 ml-auto font-mono">{group.items.length}</span>
                </div>
                <div className="space-y-1">
                  {group.items.map(emp => (
                    <PaletteDraggable key={emp.id} employee={emp} costCenter={group.kst}
                                      arbeitetNicht={!arbeitetHeute(emp)} policyName={emp.policy_name} />
                  ))}
                </div>
              </div>
            ))
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function PaletteDraggable({ employee, costCenter, arbeitetNicht, policyName }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `palette::${employee.id}`,
    data: { type: 'palette-employee', employeeId: employee.id },
  });
  // Ausgegraut, aber weiter ziehbar: Es kann sein, dass jemand an seinem
  // freien Tag einspringt. Gesperrt wäre bequemer zu programmieren, würde
  // aber den Ausnahmefall unmöglich machen – und Ausnahmen sind im Handwerk
  // die Regel.
  return (
    <div className={arbeitetNicht ? 'opacity-45 grayscale' : ''}
         title={arbeitetNicht
           ? `Arbeitet heute laut Richtlinie${policyName ? ` „${policyName}"` : ''} nicht. Einplanen ist möglich, erscheint dann aber als Hinweis im Dashboard.`
           : undefined}>
      <EmployeeListItem
        employee={employee}
        costCenter={costCenter}
        setNodeRef={setNodeRef}
        dndAttributes={attributes}
        dndListeners={listeners}
        dragging={isDragging}
      />
    </div>
  );
}
